import { useSelectedLabelsStore } from "@/stores/selectedLabels";
import { useAuthStore } from "@/stores/auth";
import { useIndoStore } from "@/stores/indo";
import { usePedidosStore } from "@/stores/pedidos";
import { useOfertasStore } from "@/stores/ofertas";

import { createLabelGetters } from "./functionPdfGafas/labelHelpers";
import { createOrUpdateLoader, removeLoader } from "./functionPdfGafas/loader";
import { generarCodigoBarras } from "./functionPdfGafas/barcode";
import {
  obtenerDatosAmbosOjos,
  formateaGraduacionResultante,
  createDatosCache,
  type DatosCache,
  type DatosOjos,
} from "./functionPdfGafas/datosAdicionales";
import { captureLensesImage, calcularFitDinamico } from "./functionPdfGafas/lensesCapture";
import { buildDocDefinition } from "./functionPdfGafas/docDefinition";
import { getPdfMake, getPdfLib, warmupPdfLibs } from "./functionPdfGafas/lazyLibs";
import type { PdfContext } from "./functionPdfGafas/types";

/** Compat Vuex `{ state: { auth, indo, pedidos } }` para código legacy del PDF */
function getPiniaAsVuexLikeState() {
  const auth = useAuthStore();
  const indo = useIndoStore();
  const pedidos = usePedidosStore();
  const ofertas = useOfertasStore();
  return {
    auth: { token: auth.token ?? auth.tokenManager },
    pedidos: pedidos.$state,
    indo: {
      ...indo.$state,
      caracteristicas: ofertas.caracteristicas,
    },
  };
}

export function usePdfGafas(_store?: any) {
  const selectedLabelsStore = useSelectedLabelsStore();
  const store = _store ?? { get state() { return getPiniaAsVuexLikeState(); } };
  const labelGetters = createLabelGetters(selectedLabelsStore);

  const generarPDF = async (
    usarImagenCacheada: boolean = false,
    pedido: any,
    current: number = 1,
    total: number = 1,
    opts?: {
      useOffscreenLensesOnly?: boolean;
      awaitDownload?: boolean;
      returnBuffer?: boolean;
      datosCache?: DatosCache;
      // Datos adicionales [OD, OI] ya pre-fetchados por el caller (pipeline en
      // batch). Si está presente se salta el `obtenerDatosAmbosOjos` interno.
      prefetchedDatos?: Promise<DatosOjos> | DatosOjos;
    },
  ): Promise<Uint8Array | void> => {
    const ofertasStore = useOfertasStore();
    const pedidosStore = usePedidosStore();
    createOrUpdateLoader(current, total);

    // Pre-disparar la descarga de pdfmake/html2canvas/pdf-lib en paralelo
    // con los SOAPs siguientes. Idempotente: si ya están cacheadas es un no-op.
    warmupPdfLibs();

    // Limpiar datos de cálculo solo si viene del formulario y no hay datos
    // calculados frescos en el pedido actual.
    const hayDatosCalculados = pedido?.oClaseBAPI?.ojoOutDerecho || pedido?.oClaseBAPI?.ojoOutIzquierdo;
    if (usarImagenCacheada && pedido?.oClaseBAPI && !hayDatosCalculados) {
      delete pedido.oClaseBAPI.ojoOutDerecho;
      delete pedido.oClaseBAPI.ojoOutIzquierdo;
      delete pedido.oClaseBAPI.datosRetornoDerecho;
      delete pedido.oClaseBAPI.datosRetornoIzquierdo;
    }

    const token = store?.state?.auth?.token;
    const [datosAdicionalesderecha, datosAdicionalesizquierda] = await (
      opts?.prefetchedDatos ?? obtenerDatosAmbosOjos(pedido, token, opts?.datosCache)
    );
    formateaGraduacionResultante(pedido);

    try {
      const capture = await captureLensesImage(
        pedido,
        usarImagenCacheada,
        opts?.useOffscreenLensesOnly ?? false,
      );
      const fitLentes = await calcularFitDinamico(pedido, capture, store, ofertasStore);

      const pdfMake = await getPdfMake();

      const ctx: PdfContext = {
        pedido,
        store,
        pedidosStore,
        selectedLabelsStore,
        labelGetters,
        datosAdicionalesderecha,
        datosAdicionalesizquierda,
        imagenLente: capture.imagenLente,
        textoOD: capture.textoOD,
        textoOI: capture.textoOI,
        fitLentes,
        generarCodigoBarras,
      };

      const docDefinition = buildDocDefinition(ctx);

      const vbeln = (pedido?.oClaseBAPI?.VBELN ?? "").replace(/[<>:\"/\\|?*]/g, "");
      const safeFilename = `pedido_${vbeln}.pdf`;
      // info.title hace que el filename por defecto (al guardar desde el visor
      // del navegador, cuando se abre en pestaña) sea también `pedido_VBELN`.
      (docDefinition as any).info = { title: `pedido_${vbeln}` };
      const pdfDoc = pdfMake.createPdf(docDefinition);

      if (opts?.returnBuffer) {
        return new Promise<Uint8Array>((resolve, reject) => {
          try {
            pdfDoc.getBuffer((buffer: Uint8Array) => resolve(buffer));
          } catch (e) {
            reject(e);
          }
        });
      }
      if (opts?.awaitDownload) {
        await new Promise<void>((resolve, reject) => {
          try {
            pdfDoc.download(safeFilename, () => resolve());
          } catch (e) {
            reject(e);
          }
        });
      } else if (total > 1) {
        pdfDoc.download(safeFilename);
      } else {
        pdfDoc.open();
      }
    } catch (error) {
      alert("Error al generar PDF: " + (error as Error).message);
    } finally {
      if (current >= total) removeLoader();
    }
  };

  const generarPDFMultiple = async (
    pedidos: any[],
    usarImagenCacheada = false,
    opts?: { useOffscreenLensesOnly?: boolean },
  ): Promise<void> => {
    if (pedidos.length === 0) return;

    // Disparamos en paralelo la carga de pdfmake/html2canvas/pdf-lib antes
    // del bucle para que el primer PDF no pague la latencia de descarga.
    warmupPdfLibs();

    const { PDFDocument } = await getPdfLib();
    const total = pedidos.length;
    const mergedPdf = await PDFDocument.create();
    // Cache de SOAPs (lente/tratamientos/prismas) compartida por todo el batch:
    // pedidos del mismo cliente con mismos códigos comparten la misma promesa.
    const datosCache = createDatosCache();

    // Pipeline: arrancamos los SOAPs del pedido[0] ya, y dentro del bucle
    // disparamos los del siguiente antes de renderizar el actual. El render
    // (html2canvas/pdfmake) es serial — comparte DOM — pero los SOAPs no, así
    // que solapan con el render del pedido anterior.
    const token = store?.state?.auth?.token;
    const prefetch = (idx: number): Promise<DatosOjos> | undefined => {
      if (idx >= pedidos.length) return undefined;
      const p = obtenerDatosAmbosOjos(pedidos[idx], token, datosCache);
      // Adjuntamos un handler vacío para silenciar el "unhandled rejection"
      // mientras la promesa espera a ser awaitada en la próxima iteración;
      // el await reposterior re-lanza el error igual.
      p.catch(() => {});
      return p;
    };

    let nextDatos = prefetch(0);

    for (let i = 0; i < pedidos.length; i++) {
      createOrUpdateLoader(i + 1, total);
      const currentDatos = nextDatos;
      nextDatos = prefetch(i + 1);
      try {
        const buffer = await generarPDF(usarImagenCacheada, pedidos[i], i + 1, total, {
          ...opts,
          returnBuffer: true,
          datosCache,
          prefetchedDatos: currentDatos,
        }) as Uint8Array;

        const pdf = await PDFDocument.load(buffer);
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        copiedPages.forEach((page) => mergedPdf.addPage(page));
      } catch (err) {
        console.error(`❌ Error procesando pedido ${i + 1}:`, err);
      }
    }

    const mergedBytes = await mergedPdf.save();
    const blob = new Blob([mergedBytes.buffer as ArrayBuffer], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    // Si solo hay un pedido, usar `pedido_<vbeln>`; con varios, `pedidos_<fecha>_<hh-mm>`.
    if (pedidos.length === 1) {
      const vbeln = (pedidos[0]?.oClaseBAPI?.VBELN ?? "").replace(/[<>:\"/\\|?*]/g, "");
      a.download = `pedido_${vbeln}.pdf`;
    } else {
      const now = new Date();
      const stamp = `${now.toISOString().slice(0, 10)}_${String(now.getHours()).padStart(2, "0")}-${String(now.getMinutes()).padStart(2, "0")}`;
      a.download = `pedidos_${stamp}.pdf`;
    }
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    removeLoader();
  };

  return {
    generarPDF,
    generarPDFMultiple,
  };
}
